import { useMutation } from '@tanstack/react-query';
import {
  type FileDownloadResponse,
  type MarkWithoutInvoiceRequest,
  type MovementSubjectPayment,
  UPLOAD_EXTENSIONS,
} from '@k-management/shared';
import { useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Field } from '../../components/Field.js';
import { apiRequest, apiUpload } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';

/** What a payment without invoice says of itself (3.14). */
export interface NoInvoiceValues {
  reference: string;
  periodFrom: string;
  periodTo: string;
}

export const EMPTY_NO_INVOICE: NoInvoiceValues = { reference: '', periodFrom: '', periodTo: '' };

/** The body of the values: empty fields as null. */
export function noInvoiceBody(values: NoInvoiceValues): MarkWithoutInvoiceRequest {
  return {
    reference: values.reference.trim() || null,
    periodFrom: values.periodFrom || null,
    periodTo: values.periodTo || null,
  };
}

/** Reference and period of a payment to a subject that does not invoice. */
export function NoInvoiceFields({
  values,
  onChange,
}: {
  values: NoInvoiceValues;
  onChange: (values: NoInvoiceValues) => void;
}) {
  return (
    <>
      <Field label="Riferimento" hint="La polizza, il verbale, la quietanza.">
        <input
          value={values.reference}
          maxLength={200}
          placeholder="Es. Polizza RC 123 · rata 2025"
          onChange={(event) => onChange({ ...values, reference: event.target.value })}
        />
      </Field>
      <div className="row">
        <Field label="Competenza dal">
          <input
            type="date"
            value={values.periodFrom}
            onChange={(event) => onChange({ ...values, periodFrom: event.target.value })}
          />
        </Field>
        <Field label="al">
          <input
            type="date"
            value={values.periodTo}
            onChange={(event) => onChange({ ...values, periodTo: event.target.value })}
          />
        </Field>
      </div>
    </>
  );
}

/** The period of a payment without invoice, as written on a line. */
export function periodLabel(payment: Pick<MovementSubjectPayment, 'periodFrom' | 'periodTo'>) {
  if (payment.periodFrom && payment.periodTo) {
    return `dal ${formatDate(payment.periodFrom)} al ${formatDate(payment.periodTo)}`;
  }
  if (payment.periodFrom) return `dal ${formatDate(payment.periodFrom)}`;
  if (payment.periodTo) return `fino al ${formatDate(payment.periodTo)}`;
  return null;
}

/**
 * On a line of a payment to a subject (3.14): without invoice, its reference,
 * period and receipt (open, attach); recorded before the subject was marked,
 * «È senza fattura».
 */
export function NoInvoiceActions({
  payment,
  canWrite,
  onDone,
}: {
  payment: MovementSubjectPayment;
  canWrite: boolean;
  onDone: () => void;
}) {
  const [marking, setMarking] = useState(false);
  const [values, setValues] = useState<NoInvoiceValues>(EMPTY_NO_INVOICE);
  const base = `/treasury/subject-payments/${payment.id}`;
  const open = useMutation({
    mutationFn: () => apiRequest<FileDownloadResponse>(`${base}/document`),
    onSuccess: (link) => window.open(link.url, '_blank', 'noopener'),
  });
  const attach = useMutation({
    mutationFn: (file: File) =>
      apiUpload(`${base}/document?name=${encodeURIComponent(file.name)}`, file),
    onSuccess: onDone,
  });
  const mark = useMutation({
    mutationFn: () =>
      apiRequest(`${base}/without-invoice`, { method: 'POST', body: noInvoiceBody(values) }),
    onSuccess: () => {
      setMarking(false);
      onDone();
    },
  });
  if (!payment.withoutInvoice) {
    if (!payment.subjectNoInvoice || !canWrite || payment.available !== payment.amount) {
      return null;
    }
    return (
      <>
        {' '}
        <button type="button" className="link" onClick={() => setMarking(true)}>
          È senza fattura
        </button>
        {marking && (
          <ConfirmDialog
            title="Pagamento senza fattura"
            confirmLabel="È senza fattura"
            busy={mark.isPending}
            error={mark.error?.message}
            onConfirm={() => mark.mutate()}
            onCancel={() => setMarking(false)}
          >
            <p className="small">
              {payment.subjectName} non emette fattura: il pagamento diventa il costo e non resta
              più da usare su una fattura.
            </p>
            <NoInvoiceFields values={values} onChange={setValues} />
          </ConfirmDialog>
        )}
      </>
    );
  }
  const period = periodLabel(payment);
  return (
    <small className="block muted">
      {payment.reference && <>{payment.reference} · </>}
      {period && <>{period} · </>}
      {payment.documentName ? (
        <button
          type="button"
          className="link"
          disabled={open.isPending}
          onClick={() => open.mutate()}
        >
          {payment.documentName}
        </button>
      ) : (
        'nessun documento'
      )}
      {canWrite && (
        <label className="link">
          {' '}
          · {payment.documentName ? 'sostituisci' : 'allega'}
          <input
            type="file"
            hidden
            accept={UPLOAD_EXTENSIONS.map((extension) => `.${extension}`).join(',')}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) attach.mutate(file);
            }}
          />
        </label>
      )}
      {(open.error ?? attach.error) && (
        <span className="error block">{(open.error ?? attach.error)!.message}</span>
      )}
    </small>
  );
}
